import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { I18nProvider, SiteProvider } from "@/lib/i18n";
import { AuthProvider, useAuth, isStaff } from "@/lib/auth";
import { CartProvider } from "@/lib/cart";
import { api, errMsg } from "@/lib/api";
import { track } from "@/lib/analytics";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { WhatsAppButton, CookieBanner } from "@/components/Floating";
import Home from "@/pages/Home";
import About from "@/pages/About";
import { ServicesPage, ServiceDetail } from "@/pages/Services";
import { SolutionsPage, ProductDetail } from "@/pages/Solutions";
import { ClientsPage, CaseDetail } from "@/pages/Clients";
import { ResourcesPage, ArticleDetail, FaqPage } from "@/pages/Resources";
import { ContactPage, DemoPage } from "@/pages/Forms";
import { CartPage, CheckoutPage, PaymentMock, PaymentResult } from "@/pages/Shop";
import { LoginPage, PortalPage } from "@/pages/Account";
import LegalPage from "@/pages/Legal";
import AdminApp from "@/admin/AdminApp";

function AuthCallback() {
  const done = useRef(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    api
      .post("/auth/google/session", { session_id: sid })
      .then(({ data }) => {
        setUser(data);
        navigate(isStaff(data) ? "/admin" : "/portal", { replace: true });
      })
      .catch((e) => {
        toast.error(errMsg(e));
        navigate("/acceso", { replace: true });
      });
  }, [navigate, setUser]);
  return <div className="min-h-screen grid place-items-center text-slate-500">Validando sesión…</div>;
}

function Public({ children }) {
  return (
    <>
      <Header />
      <main id="main" className="min-h-[60vh]">{children}</main>
      <Footer />
      <WhatsAppButton />
    </>
  );
}

function RequireAuth({ children, staff }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="min-h-screen grid place-items-center text-slate-500">Cargando…</div>;
  if (!user) return <Navigate to={`/acceso?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (staff && !isStaff(user)) return <Navigate to="/portal" replace />;
  return children;
}

function Router() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    track("PageView", { path: location.pathname });
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  const P = (el) => <Public>{el}</Public>;
  return (
    <Routes>
      <Route path="/" element={P(<Home />)} />
      <Route path="/nosotros" element={P(<About />)} />
      <Route path="/servicios" element={P(<ServicesPage />)} />
      <Route path="/servicios/:slug" element={P(<ServiceDetail />)} />
      <Route path="/soluciones" element={P(<SolutionsPage />)} />
      <Route path="/soluciones/:slug" element={P(<ProductDetail />)} />
      <Route path="/clientes" element={P(<ClientsPage />)} />
      <Route path="/casos/:slug" element={P(<CaseDetail />)} />
      <Route path="/recursos" element={P(<ResourcesPage />)} />
      <Route path="/recursos/:slug" element={P(<ArticleDetail />)} />
      <Route path="/preguntas-frecuentes" element={P(<FaqPage />)} />
      <Route path="/contacto" element={P(<ContactPage />)} />
      <Route path="/demo" element={P(<DemoPage />)} />
      <Route path="/carrito" element={P(<CartPage />)} />
      <Route path="/checkout" element={P(<RequireAuth><CheckoutPage /></RequireAuth>)} />
      <Route path="/pago/simulado" element={P(<RequireAuth><PaymentMock /></RequireAuth>)} />
      <Route path="/pago/resultado" element={P(<PaymentResult />)} />
      <Route path="/acceso" element={P(<LoginPage />)} />
      <Route path="/portal" element={P(<RequireAuth><PortalPage /></RequireAuth>)} />
      <Route path="/legal/:doc" element={P(<LegalPage />)} />
      <Route path="/admin/*" element={<RequireAuth staff><AdminApp /></RequireAuth>} />
      <Route path="*" element={P(<div className="wrap py-32 text-center"><h1 className="text-4xl font-bold">404</h1><p className="lead mt-3">Página no encontrada.</p></div>)} />
    </Routes>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <SiteProvider>
        <AuthProvider>
          <CartProvider>
            <BrowserRouter>
              <Router />
              <CookieBanner />
              <Toaster richColors position="top-right" />
            </BrowserRouter>
          </CartProvider>
        </AuthProvider>
      </SiteProvider>
    </I18nProvider>
  );
}
